<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Fund } from './fundConfig'

const props = defineProps<{
  fund: Fund
  fvaPredictedBps?: number | null
  fvaResidualBps?: number | null
  fvaShadow?: boolean
  // Feature the prediction was computed from (JH: step-aside s_on; BGA: the
  // 07:40 m_t freeze), fraction already converted to bps by the parent.
  fvaMtBps?: number | null
  // Committed plan + execution state for the popover's position line. These
  // describe today's PLAN, not proven live holdings (committed action ≠ live
  // state) — the copy says "today's plan" for exactly that reason.
  decision?: string | null
  execStatus?: 'executed' | 'hold' | 'pending' | null
}>()

const testId = `fva-${props.fund.toLowerCase()}`

// FVA is wired live for BGA (`gap_correct` band mode, trigger ≥60 bps)
// and JH (`gap_correct_ramp`, ramp low knot 35 bps — same threshold as
// engine.py's step_aside.lambda_ramp). GOLD and GGE render the
// "not-yet-wired" placeholder until their arcs land.
const isFvaWired = computed(() => props.fund === 'BGA' || props.fund === 'JH')

const triggerThresholdBps = computed(() => (props.fund === 'JH' ? 35 : 60))

const triggerActive = computed(() =>
  props.fvaPredictedBps != null
    && Math.abs(props.fvaPredictedBps) >= triggerThresholdBps.value,
)

function fmtBps(v: number | null | undefined): string {
  if (v == null) return '—'
  const sign = v >= 0 ? '+' : ''
  return `${sign}${v.toFixed(1)} bps`
}

function valClass(v: number | null | undefined): string {
  if (v == null || v === 0) return ''
  return v > 0 ? 'positive' : 'negative'
}

const subLine = computed(() =>
  `will surface predicted vs realized when the ${props.fund} FVA arc lands`,
)

// --- (i) popover: what the predicted value means, with actual numbers ------

const infoOpen = ref(false)

const predicted = computed(() => props.fvaPredictedBps ?? 0)
const positive = computed(() => predicted.value >= 0)

// λ = predicted / m_t (both bps, same sign) — guard the degenerate cases.
const lambdaDisplay = computed(() => {
  const m = props.fvaMtBps
  if (m == null || m === 0 || props.fvaPredictedBps == null) return null
  return (props.fvaPredictedBps / m).toFixed(2)
})

const capturedBps = computed(() => {
  const m = props.fvaMtBps
  if (m == null || props.fvaPredictedBps == null) return null
  return m - props.fvaPredictedBps
})

// Direction: a positive echo means the official strike lags the up-move, so
// the synthetic (full move) prints above official — synthetic temporarily
// over-values. Negative echo is the mirror.
const directionLine = computed(() => {
  const p = Math.abs(predicted.value).toFixed(1)
  const side = positive.value ? 'above official' : 'below official'
  const valuing = positive.value ? 'over-values' : 'under-values'
  const capture = capturedBps.value != null
    ? `Today's official strike is expected to capture only ~${fmtBps(capturedBps.value)} of the move — synthetic`
    : 'Synthetic'
  return `${capture} NAV should read ≈ ${p} bps ${side} at the strike (synthetic temporarily ${valuing} vs official NAV).`
})

const positionLine = computed(() => {
  const echoWord = positive.value
    ? `the ${fmtBps(predicted.value)} echo is an expected tailwind in tomorrow's official NAV`
    : `the ${fmtBps(predicted.value)} echo is expected drag on tomorrow's official NAV`
  if (props.decision === 'buy' && props.execStatus === 'executed')
    return `Today's plan is BUY (executed) — while invested, ${echoWord}.`
  if (props.decision === 'sell' && props.execStatus === 'executed')
    return `Today's plan was SELL (executed) — in cash the echo does not affect P&L; it explains why today's synth-vs-official gap looks stretched.`
  return `If invested, a positive echo is an expected tailwind (negative = drag) in tomorrow's official NAV; in cash it only explains today's synth-vs-official gap.`
})

const shadowLine = computed(() => {
  if (!props.fvaShadow) return 'Applied to the live signal before thresholding.'
  return props.fund === 'JH'
    ? 'Shadow only — never applied. Today\'s JH decision comes from the step-aside rule on the same futures move.'
    : 'Shadow only — never applied to the live signal.'
})
</script>

<template>
  <div class="fva-card" :data-testid="testId">
    <div class="section-header">{{ fund }} FVA</div>

    <template v-if="isFvaWired">
      <div v-if="triggerActive" class="fva-row" data-testid="fva-trigger-row">
        <span class="fva-label">
          FVA{{ fvaShadow ? ' (shadow)' : '' }}: predicted {{ fmtBps(fvaPredictedBps) }}
          <button
            class="fva-info-btn"
            data-testid="fva-info-btn"
            type="button"
            aria-label="What does this prediction mean?"
            @click.stop="infoOpen = !infoOpen"
          >i</button>
        </span>
        <span class="fva-value" :class="valClass(fvaResidualBps)">
          model err {{ fmtBps(fvaResidualBps) }}
        </span>
      </div>
      <div v-else class="fva-dormant">
        <!-- A live sub-threshold value must stay visible: a bare "dormant"
             is indistinguishable from "nothing computed" and reads as a
             regression when the echo merely dips below the trigger. -->
        <template v-if="fvaPredictedBps != null">
          {{ fund === 'JH' ? 'echo' : 'predicted' }} {{ fmtBps(fvaPredictedBps) }}
          — below {{ triggerThresholdBps }} bps trigger (model dormant)
        </template>
        <template v-else>
          no trigger today — model dormant
        </template>
      </div>

      <div
        v-if="infoOpen && triggerActive"
        class="fva-info-popover"
        data-testid="fva-info-popover"
      >
        <div class="fva-info-title">What this predicts</div>

        <template v-if="fund === 'JH'">
          <p v-if="fvaMtBps != null">
            Overnight NQ futures move (m_t, frozen 08:40 UK): {{ fmtBps(fvaMtBps) }}.
            <template v-if="lambdaDisplay != null">
              Ramp λ ≈ {{ lambdaDisplay }} at this size → predicted echo
              = λ × m_t = {{ fmtBps(fvaPredictedBps) }}.
            </template>
          </p>
          <p>{{ directionLine }}</p>
          <p>
            Not a mispricing to fade — the gap is predicted to arrive in the
            next trading day's official NAV (2026-07-23 payoff study,
            OOS IC +0.205).
          </p>
          <p>{{ positionLine }}</p>
        </template>

        <template v-else>
          <p>
            BGA band mode (λ = 0.79, 60–150 bps band): the correction removes
            the component of the overnight move BG's strike already
            fair-values and restores yesterday's mechanical reversal.
          </p>
          <p>{{ directionLine }}</p>
          <p>{{ positionLine }}</p>
        </template>

        <p class="fva-info-shadow">{{ shadowLine }}</p>
      </div>
    </template>

    <template v-else>
      <div class="fva-placeholder">
        FVA — not yet wired
      </div>
      <div class="fva-placeholder-sub">
        {{ subLine }}
      </div>
    </template>
  </div>
</template>

<style scoped>
.section-header {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #666;
  margin-bottom: 0.5rem;
}
.fva-card {
  border: 1px solid #1a2a1a;
  border-radius: 6px;
  padding: 0.75rem;
  background: #0d1a0d;
  position: relative;
}
.fva-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 12px;
  font-family: monospace;
}
.fva-label { color: #888; }
.fva-value { color: #ccc; font-weight: 600; }
.fva-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-left: 4px;
  padding: 0;
  border: 1px solid #4a5a4a;
  border-radius: 50%;
  background: transparent;
  color: #8a9a8a;
  font-size: 9px;
  font-style: italic;
  font-family: Georgia, serif;
  cursor: pointer;
  vertical-align: middle;
}
.fva-info-btn:hover { color: #cfd8cf; border-color: #6a7a6a; }
.fva-info-popover {
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid #2a3a2a;
  border-radius: 6px;
  background: #101d10;
  color: #aab5aa;
  font-size: 11px;
  line-height: 1.45;
}
.fva-info-popover p { margin: 0 0 6px; }
.fva-info-popover p:last-child { margin-bottom: 0; }
.fva-info-title {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #667a66;
  margin-bottom: 4px;
}
.fva-info-shadow { color: #7a8a7a; font-style: italic; }
.fva-dormant, .fva-placeholder {
  color: #666;
  font-size: 12px;
  font-style: italic;
  padding: 4px 0;
}
.fva-placeholder-sub {
  color: #555;
  font-size: 10px;
  padding-top: 2px;
}
.positive { color: #4caf50 !important; }
.negative { color: #f44336 !important; }
</style>
